/**
 * GlobalParticleManager provides global VFX system management functionality.
 * Manages VFX particles and emitters for fire-and-forget VFX spawning with automatic lifetime management.
 */
import * as THREE from 'three';


import { NetRuntime } from '../multiplayer/NetRuntime.js';
import { AssetPath } from '../storage/storageProvider.js';
import { resourceManager } from '../utils/ResourceManager.js';
import * as VFX from '../vfx/index.js';
import { VFXDefinition } from '../vfx/types.js';

import { Actor } from './Actor.js';


// Type aliases for the classes
type VFXEmitter = InstanceType<typeof VFX.VFXEmitterCore>;
type VFXParticles = InstanceType<typeof VFX.VFXParticlesCore>;

import type { VFXEmitterSettings, VFXParticlesSettings } from '../vfx/types.js';
import type { VFXDefinitionJSON } from '../vfx/types.js';

function isSerializedResource(value: unknown): value is { $version: number; $root: unknown } {
  return typeof value === 'object' && value !== null &&
    '$version' in value &&
    typeof (value as { $version?: unknown }).$version === 'number' &&
    '$root' in value;
}

export interface AttachedVFXInfo {
  emitter: VFXEmitter;
  attachTime: number;
  lifetime: number;
}

// We don't need to register the class, because it should not be instantiated, and it's
// always transient. We could also drop the base class, but since it has a child component,
// this solution is easier.
// @EngineClass('GlobalParticleManager')
export class GlobalParticleManager extends Actor {
  private attachedVFX: AttachedVFXInfo[] = [];
  private vfxParticlesRegistry: Map<string, VFXParticles> = new Map();
  /** Tracks in-progress VFXParticles creation to prevent duplicate registration */
  private pendingVFXParticlesCreation: Map<string, Promise<VFXParticles>> = new Map();

  private normalizeVFXAlphaTexture(texture: THREE.Texture): void {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearFilter;
    texture.needsUpdate = true;
  }



  public override doEndPlay(): void {
    // Skip VFX cleanup in headless mode (no rendering)
    if (NetRuntime.isDedicatedServer()) {
      super.doEndPlay();
      return;
    }

    this.cleanupAllVFX();
    this.cleanupVFXParticles();
    super.doEndPlay();
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    // Skip VFX updates in headless mode (no rendering)
    if (NetRuntime.isDedicatedServer()) {
      return;
    }

    this.updateVFXLifecycles(deltaTime);
    this.updateVFXParticles();
  }

  private updateVFXLifecycles(deltaTime: number): void {
    const world = this.getWorld();
    if (!world) return;

    const currentTime = world.getGameTime();
    const remainingVFX: AttachedVFXInfo[] = [];

    for (const vfxInfo of this.attachedVFX) {
      // Update the emitter with global time, not elapsed time
      vfxInfo.emitter.update(currentTime, deltaTime);

      const elapsedTime = currentTime - vfxInfo.attachTime;
      if (elapsedTime < vfxInfo.lifetime) {
        remainingVFX.push(vfxInfo);
      } else {
        vfxInfo.emitter.removeFromParent();
      }
    }

    this.attachedVFX = remainingVFX;
  }

  private updateVFXParticles(): void {
    const world = this.getWorld();
    if (!world) return;

    const elapsedTime = world.getGameTime();
    for (const vfxParticles of this.vfxParticlesRegistry.values()) {
      vfxParticles.update(elapsedTime);
    }
  }

  public cleanupAllVFX(): void {
    for (const vfxInfo of this.attachedVFX) {
      vfxInfo.emitter.removeFromParent();
    }
    this.attachedVFX = [];
  }

  public cleanupVFXParticles(): void {
    const world = this.getWorld();
    const store = VFX.vfxStore.getState();

    for (const [name, vfxParticles] of this.vfxParticlesRegistry) {
      // Unregister from the wawa-vfx global store to prevent "already exists" warnings on reload
      store?.unregisterEmitter(name);

      // Remove mesh from scene
      if (world) {
        world.scene.remove(vfxParticles.getMesh());
      }

      // Dispose VFXParticles
      vfxParticles.dispose();
    }

    this.vfxParticlesRegistry.clear();
    this.pendingVFXParticlesCreation.clear();
  }

  /**
   * Get or create a VFXParticles instance for the given name
   * @param name - Unique identifier for the VFX particles system
   * @param settings - VFX particles settings
   * @returns - The VFXParticles instance
   */
  public async getOrCreateVFXParticles(name: string, settings: VFXParticlesSettings, alphaMap?: THREE.Texture, geometry?: THREE.BufferGeometry): Promise<VFXParticles> {
    // Skip in headless mode (no rendering) - return a dummy object
    if (NetRuntime.isDedicatedServer()) {
      // Return a minimal mock object to prevent crashes
      return {} as VFXParticles;
    }

    // Check if already created
    const existingParticles = this.vfxParticlesRegistry.get(name);
    if (existingParticles) {
      return existingParticles;
    }

    // Check if creation is already in progress (prevents race condition with concurrent calls)
    const pendingCreation = this.pendingVFXParticlesCreation.get(name);
    if (pendingCreation) {
      return pendingCreation;
    }

    // Create the particles and track the promise to prevent duplicate creation
    const creationPromise = this.createVFXParticlesInternal(name, settings, alphaMap, geometry);
    this.pendingVFXParticlesCreation.set(name, creationPromise);

    try {
      const vfxParticles = await creationPromise;
      return vfxParticles;
    } finally {
      // Clean up pending promise after completion (success or failure)
      this.pendingVFXParticlesCreation.delete(name);
    }
  }

  private async createVFXParticlesInternal(name: string, settings: VFXParticlesSettings, alphaMap?: THREE.Texture, geometry?: THREE.BufferGeometry): Promise<VFXParticles> {
    // Convert settings to wawa-vfx format
    // Note: frustumCulled is always false because particle systems have particles
    // that travel far from the emitter origin. When the emitter is outside the camera frustum
    // but particles are still visible, frustum culling would incorrectly hide all particles.
    // The frustumCulled setting in VFXParticlesSettings is ignored for backwards compatibility.
    const internalSettings: any = {
      nbParticles: settings.nbParticles ?? 1000,
      intensity: settings.intensity ?? 1,
      renderMode: settings.renderMode ?? VFX.RenderModeEnum.Billboard,
      stretchScale: settings.stretchScale ?? 1.0,
      fadeSize: settings.fadeSize ?? [0.1, 0.9],
      fadeAlpha: settings.fadeAlpha ?? [0, 1.0],
      gravity: settings.gravity ?? [0, 0, 0],
      frustumCulled: false, // Always disabled - see comment above
      appearance: (() => {
        const a = settings.appearance as string | number | undefined;
        if (a === 'circular' || a === VFX.AppearanceMode.Circular) {
          return VFX.AppearanceMode.Circular;
        }
        if (a === 'square' || a === VFX.AppearanceMode.Square) {
          return VFX.AppearanceMode.Square;
        }
        return VFX.AppearanceMode.Square;
      })(),
      easeFunction: settings.easeFunction ?? 'easeLinear',
      shadingHooks: settings.shadingHooks ?? {},
    };

    // Convert blending mode string to THREE constant
    if (settings.blendingMode !== undefined) {
      const b = settings.blendingMode as string | number;
      if (typeof b === 'number') {
        internalSettings.blendingMode = b;
      } else {
        switch (b) {
          case 'normal': internalSettings.blendingMode = THREE.NormalBlending; break;
          case 'additive': internalSettings.blendingMode = THREE.AdditiveBlending; break;
          case 'subtractive': internalSettings.blendingMode = THREE.SubtractiveBlending; break;
          case 'multiply': internalSettings.blendingMode = THREE.MultiplyBlending; break;
        }
      }
    }

    // Load alpha map if specified
    if (settings.alphaMapPath && !alphaMap) {
      const world = this.getWorld();
      if (world) {
        const assetPath = new AssetPath(settings.alphaMapPath);
        const loadedTexture = await resourceManager.loadTexture(assetPath);
        if (loadedTexture) {
          alphaMap = loadedTexture;
          this.normalizeVFXAlphaTexture(alphaMap);
        }
      }
    }

    if (alphaMap) {
      this.normalizeVFXAlphaTexture(alphaMap);
    }

    // Load geometry if specified, or use default cube for mesh mode
    if (settings.geometryPath && !geometry) {
      const world = this.getWorld();
      if (world) {
        const assetPath = new AssetPath(settings.geometryPath);
        const gltf = await resourceManager.loadModel(assetPath);
        if (gltf && gltf.scene.children[0] instanceof THREE.Mesh) {
          geometry = (gltf.scene.children[0] as THREE.Mesh).geometry;
        }
      }
    } else if (settings.renderMode === 'mesh') {
      // Use default cube geometry for mesh mode
      geometry = new THREE.BoxGeometry(1, 1, 1);
    }

    const renderer = this.getWorld()?.gameLoop?.renderer?.native;
    const native = renderer?.asWebGL() ?? renderer?.asWebGPU();
    const backend = VFX.resolveVFXParticlesBackend({ renderer: native });

    // Create VFXParticles using the active renderer class so explicit WebGL and
    // WebGPU-backed paths can supply backend-compatible materials.
    const vfxParticles = new VFX.VFXParticlesCore(
      name,
      internalSettings,
      VFX.vfxStore.getState(),
      alphaMap,
      geometry,
      { backend }
    );

    // Always disable frustum culling on the mesh to prevent particles from disappearing
    // when the emitter origin is outside the camera view
    const mesh = vfxParticles.getMesh();
    mesh.frustumCulled = false;

    // Add to scene
    const world = this.getWorld();
    if (world) {
      world.scene.add(mesh);
    }

    this.vfxParticlesRegistry.set(name, vfxParticles);

    return vfxParticles;
  }

  /**
   * Spawn a VFX effect from code (supports single or multiple emitters/particles)
   * @param name - Unique name for the VFX
   * @param particlesSettings - Particle system settings (single or array)
   * @param emitterSettings - Emitter settings (single or array)
   * @param options - Spawn options
   * @returns - Array of created emitters
   */
  public async spawnVFX(
    name: string,
    particlesSettings: VFXParticlesSettings | VFXParticlesSettings[],
    emitterSettings: VFXEmitterSettings | VFXEmitterSettings[],
    options: {
      position?: THREE.Vector3;
      rotation?: THREE.Euler;
      scale?: THREE.Vector3;
    } = {}
  ): Promise<VFXEmitter[]> {
    // Skip in headless mode (no rendering)
    if (NetRuntime.isDedicatedServer()) {
      return [];
    }

    // Normalize to arrays
    const particlesArray = Array.isArray(particlesSettings) ? particlesSettings : [particlesSettings];
    const emittersArray = Array.isArray(emitterSettings) ? emitterSettings : [emitterSettings];

    const definition = VFXDefinition.fromJSON({
      name,
      particles: particlesArray,
      emitters: emittersArray,
    });

    return this.spawnVFXFromDefinition(definition, options);
  }

  /**
   * Spawn a VFX effect from a definition (v2 API for multiple emitters/particles)
   * @param definition - VFX definition with multiple particles and emitters
   * @param options - Spawn options
   * @returns - Array of created emitters
   */
  public async spawnVFXFromDefinition(
    definition: VFXDefinition,
    options: {
      position?: THREE.Vector3;
      rotation?: THREE.Euler;
      scale?: THREE.Vector3;
    } = {}
  ): Promise<VFXEmitter[]> {
    // Skip in headless mode (no rendering)
    if (NetRuntime.isDedicatedServer()) {
      return [];
    }

    const emitters: VFXEmitter[] = [];

    // Create all particle systems
    for (let i = 0; i < definition.particles.length; i++) {
      const particleSettings = definition.particles[i];
      const particleName = `${definition.name}_particles_${i}`;
      await this.getOrCreateVFXParticles(particleName, particleSettings);
    }

    // Create all emitters
    for (let i = 0; i < definition.emitters.length; i++) {
      const emitterSettings = definition.emitters[i];
      const particlesIndex = emitterSettings.particlesIndex ?? 0;
      const particleName = `${definition.name}_particles_${particlesIndex}`;

      const emitter = new VFX.VFXEmitterCore(
        particleName,
        emitterSettings,
        VFX.vfxStore.getState()
      );

      // Set transform
      if (options.position) {
        emitter.position.copy(options.position);
      }
      if (options.rotation) {
        emitter.rotation.copy(options.rotation);
      }
      if (options.scale) {
        emitter.scale.copy(options.scale);
      }

      // Attach to root
      this.rootComponent.add(emitter);

      // Calculate lifetime from settings
      const emitterLifetime = emitterSettings.duration ?? 1;
      const particleLifetime = emitterSettings.particlesLifetime?.[1] ?? 1;
      const totalLifetime = emitterSettings.loop ? Infinity : (emitterLifetime + particleLifetime);

      // Track the VFX
      const vfxInfo: AttachedVFXInfo = {
        emitter,
        attachTime: this.getWorld()?.getGameTime() ?? 0,
        lifetime: totalLifetime,
      };

      this.attachedVFX.push(vfxInfo);

      // Start emitting
      emitter.startEmitting(true);

      emitters.push(emitter);
    }

    return emitters;
  }

  /**
   * Spawn a VFX effect from a JSON file path (JSON-based)
   * @param vfxPath - Path to VFX definition JSON
   * @param options - Spawn options
   * @returns - Array of created emitters (v2) or single emitter (v1 compatibility)
   */
  public async spawnVFXFromPath(
    vfxPath: string,
    options: {
      position?: THREE.Vector3;
      rotation?: THREE.Euler;
      scale?: THREE.Vector3;
    } = {}
  ): Promise<VFXEmitter[]> {
    // Skip in headless mode (no rendering)
    if (NetRuntime.isDedicatedServer()) {
      return [];
    }

    const assetPath = new AssetPath(vfxPath);
    const json = await resourceManager.loadJsonObject(assetPath);

    if (!json) {
      throw new Error(`Failed to load VFX definition from ${vfxPath}`);
    }

    let definition: VFXDefinition;
    if (isSerializedResource(json)) {
      const loaded = await resourceManager.loadResource<VFXDefinition>(assetPath);
      if (!loaded?.instance) {
        throw new Error(`Failed to load VFX resource from ${vfxPath}`);
      }
      definition = loaded.instance;
    } else {
      console.warn(`[GlobalParticleManager] Loaded legacy VFX JSON from "${vfxPath}". Save this asset to migrate it to serialized resource format.`);
      definition = VFXDefinition.fromJSON(json as VFXDefinitionJSON);
    }
    return this.spawnVFXFromDefinition(definition, options);
  }

  public getVFXStore(): ReturnType<typeof VFX.vfxStore.getState> {
    return VFX.vfxStore.getState();
  }

  public getAttachedVFX(): AttachedVFXInfo[] {
    return this.attachedVFX;
  }
}
